import Styles from './styles.module.css';
import React, { useMemo, useState } from 'react';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { IoChevronBack, IoChevronForward, IoClose } from 'react-icons/io5';
import {
  Availability, Band, BANDS, BAND_LABEL, BAND_SHORT, BAND_TIME, WEEKDAY_LABEL,
  resolveBands, sameBands, ymd,
} from './shared';

type CalendarPanelProps = {
  recruitId: string;
  initial: Availability;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// band を反転しつつ、常に BANDS の並び（午前→午後→夜）で返す
const toggleBand = (list: Band[], band: Band): Band[] =>
  BANDS.filter((b) => (b === band ? !list.includes(b) : list.includes(b)));

// ③ 見学の受付枠。方式A：曜日×時間帯のベースルール ＋ 例外日（休止 / 臨時）。
// 保存先は recruits/{recruitId}.visitInfo.availability（ドット表記で1キーのみ更新）。
// 学生アプリ側の「空いているサロン検索」は別リポの実装。ここは入力・保存のみ。
const CalendarPanel: React.FC<CalendarPanelProps> = ({ recruitId, initial }) => {

  const [ weekly, setWeekly ] = useState<Record<number, Band[]>>(initial.weekly);
  const [ exceptions, setExceptions ] = useState<Record<string, { bands: Band[] }>>(initial.exceptions);

  const today = useMemo(() => startOfDay(new Date()), []);
  const [ monthCursor, setMonthCursor ] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [ selectedKey, setSelectedKey ] = useState<string | null>(null);

  const [ dirty, setDirty ] = useState(false);
  const [ saving, setSaving ] = useState(false);
  const [ saved, setSaved ] = useState(false);
  const [ error, setError ] = useState('');

  const availability: Availability = { weekly, exceptions };

  const isCurrentMonth =
    monthCursor.getFullYear() === today.getFullYear() && monthCursor.getMonth() === today.getMonth();

  const monthLabel = `${monthCursor.getFullYear()}年${monthCursor.getMonth() + 1}月`;

  // 当月のセル（先頭の空白 + 1..末日）
  const cells = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const lead = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [monthCursor]);

  const shiftMonth = (delta: number) => {
    setSelectedKey(null);
    setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const toggleWeekly = (day: number, band: Band) => {
    setWeekly((prev) => ({ ...prev, [day]: toggleBand(prev[day] ?? [], band) }));
    setDirty(true);
  };

  const toggleExceptionBand = (key: string, band: Band) => {
    const date = new Date(`${key}T00:00:00`);
    const current = resolveBands(availability, date).bands;
    setExceptions((prev) => ({ ...prev, [key]: { bands: toggleBand(current, band) } }));
    setDirty(true);
  };

  const clearException = (key: string) => {
    setExceptions((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDirty(true);
  };

  async function save() {
    if (!recruitId) return;
    setError('');

    // 例外日のうち「その曜日のルールと同じ内容」になったものは保存前に捨てる（データを綺麗に保つ）。
    const pruned: Record<string, { bands: Band[] }> = {};
    for (const key of Object.keys(exceptions)) {
      const weekday = new Date(`${key}T00:00:00`).getDay();
      if (!sameBands(exceptions[key].bands, weekly[weekday] ?? [])) {
        pruned[key] = { bands: exceptions[key].bands };
      }
    }

    setSaving(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'recruits', recruitId), {
        'visitInfo.availability': { weekly, exceptions: pruned },
      });
      setExceptions(pruned);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('見学の受付枠の保存に失敗しました', e);
      setError('保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSaving(false);
    }
  }

  const selectedDate = selectedKey ? new Date(`${selectedKey}T00:00:00`) : null;
  const selectedResolved = selectedDate ? resolveBands(availability, selectedDate) : null;
  const selectedRuleBands = selectedDate ? (weekly[selectedDate.getDay()] ?? []) : [];

  return (
    <section className={`${Styles.panel} ${Styles.calendarPanel}`}>
      <div className={Styles.panelHead}>
        <h3 className={Styles.panelTitle}>見学の受付枠</h3>
        <p className={Styles.panelDesc}>
          見学を受け付けられる曜日・時間帯を登録します。学生が空いている日で探せるようになります。
        </p>
      </div>

      {/* 曜日ごとのベースルール（時間帯 × 曜日のマトリクス） */}
      <div className={Styles.weeklyBlock}>
        <p className={Styles.subLabel}>曜日ごとの受付（毎週）</p>

        <div className={Styles.weeklyGrid}>
          <span className={Styles.weeklyCorner} />
          {WEEKDAY_LABEL.map((label, day) => (
            <span key={day} className={Styles.weeklyHead}>{label}</span>
          ))}

          {BANDS.map((band) => (
            <React.Fragment key={band}>
              <span className={Styles.weeklyRowLabel}>{BAND_LABEL[band]}</span>
              {WEEKDAY_LABEL.map((_, day) => {
                const on = (weekly[day] ?? []).includes(band);
                return (
                  <button
                    type="button"
                    key={day}
                    className={on ? `${Styles.weeklyCell} ${Styles.weeklyCellOn}` : Styles.weeklyCell}
                    onClick={() => toggleWeekly(day, band)}
                    disabled={saving}
                    aria-label={`${WEEKDAY_LABEL[day]}曜 ${BAND_LABEL[band]}`}
                    aria-pressed={on}
                  >
                    {on ? '✓' : ''}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        <ul className={Styles.bandDef}>
          {BANDS.map((band) => (
            <li key={band}><b>{BAND_LABEL[band]}</b>{BAND_TIME[band]}</li>
          ))}
        </ul>
      </div>

      {/* 月カレンダー（ルールの結果 + 例外日の編集） */}
      <div className={Styles.calBlock}>
        <div className={Styles.calNav}>
          <button
            type="button"
            className={Styles.calNavBtn}
            onClick={() => shiftMonth(-1)}
            disabled={isCurrentMonth || saving}
            aria-label="前の月"
          >
            <IoChevronBack />
          </button>
          <span className={Styles.calMonth}>{monthLabel}</span>
          <button
            type="button"
            className={Styles.calNavBtn}
            onClick={() => shiftMonth(1)}
            disabled={saving}
            aria-label="次の月"
          >
            <IoChevronForward />
          </button>
        </div>

        <div className={Styles.calGrid}>
          {WEEKDAY_LABEL.map((w) => (
            <span key={w} className={Styles.calWeekday}>{w}</span>
          ))}
          {cells.map((date, i) => {
            if (!date) return <span key={`e${i}`} className={Styles.calDayEmpty} />;
            const key = ymd(date);
            const past = startOfDay(date) < today;
            const { bands, isException } = resolveBands(availability, date);
            const selected = key === selectedKey;
            const cls = [
              Styles.calDay,
              past ? Styles.calDayPast : '',
              selected ? Styles.calDaySelected : '',
              bands.length ? Styles.calDayOpen : '',
            ].filter(Boolean).join(' ');
            return (
              <button
                type="button"
                key={key}
                className={cls}
                onClick={() => !past && setSelectedKey(selected ? null : key)}
                disabled={past || saving}
              >
                <span className={Styles.calDayNum}>
                  {date.getDate()}
                  {isException && <span className={Styles.calExDot} aria-label="例外日" />}
                </span>
                {bands.length > 0 && (
                  <span className={Styles.calDayBands}>
                    {BANDS.filter((b) => bands.includes(b)).map((b) => (
                      <span key={b} className={Styles.calBandTag}>{BAND_SHORT[b]}</span>
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className={Styles.calLegend}>
          前=午前 / 後=午後 / 夜=夜　　<span className={Styles.calExDot} /> 曜日ルールを変更した日
        </p>

        {/* 選択した日の編集 */}
        {selectedDate && selectedResolved && (
          <div className={Styles.dayEditor}>
            <div className={Styles.dayEditorHead}>
              <span className={Styles.dayEditorTitle}>
                {selectedDate.getMonth() + 1}月{selectedDate.getDate()}日（{WEEKDAY_LABEL[selectedDate.getDay()]}）
              </span>
              <button
                type="button"
                className={Styles.dayEditorClose}
                onClick={() => setSelectedKey(null)}
                aria-label="閉じる"
              >
                <IoClose />
              </button>
            </div>

            <div className={Styles.bandChips}>
              {BANDS.map((band) => {
                const on = selectedResolved.bands.includes(band);
                return (
                  <button
                    type="button"
                    key={band}
                    className={on ? `${Styles.bandChip} ${Styles.bandChipOn}` : Styles.bandChip}
                    onClick={() => toggleExceptionBand(selectedKey!, band)}
                    disabled={saving}
                  >
                    {BAND_LABEL[band]}
                  </button>
                );
              })}
            </div>

            <p className={Styles.dayEditorNote}>
              曜日ルール：{selectedRuleBands.length
                ? BANDS.filter((b) => selectedRuleBands.includes(b)).map((b) => BAND_LABEL[b]).join('・')
                : '受付なし'}
              {selectedResolved.isException && (
                <button type="button" className={Styles.dayEditorReset} onClick={() => clearException(selectedKey!)} disabled={saving}>
                  ルールに戻す
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      <div className={Styles.panelSaveRow}>
        {error && <p className={Styles.saveError}>{error}</p>}
        {saved && <p className={Styles.saveHint}>保存しました。</p>}
        <button type="button" className={Styles.saveButton} onClick={save} disabled={saving || !dirty}>
          {saving ? '保存中...' : '保存する'}
        </button>
      </div>
    </section>
  );
};

export default CalendarPanel;
